<template>
  <div class="d-flex flex-wrap mx-2">
    <!-- form inputs -->
    <v-checkbox
      v-model="download.jsonFiles"
      class="mr-2 my-1"
      label="Archivos JSON"
      hide-details="auto"
    ></v-checkbox>
    <v-checkbox v-if="TipoPlan != 'Contabilidad'"
      v-model="download.pdfFiles"
      class="mr-2 my-1"
      label="Archivos PDF"
      hide-details="auto"
    ></v-checkbox>
    <v-btn
      @click.stop="onClickDownload"
      color="primary"
      :disabled="!download.jsonFiles && !download.pdfFiles || loading || data.length === 0"
    >
      <v-icon>mdi-download</v-icon>
      {{ loading ? 'Procesando...' : `Descargar ${data.length} factura${data.length !== 1 ? 's' : ''}` }}
    </v-btn>

    <!-- Progress bar -->
    <div v-if="loading" class="w-100 mt-3">
      <v-progress-linear 
        :value="progress" 
        height="20"
        color="primary"
      >
        <strong>{{ Math.ceil(progress) }}%</strong>
      </v-progress-linear>
      <div class="text-center mt-2">
        Procesando {{ currentInvoice }} de {{ totalInvoices }} facturas
      </div>
    </div>

    <!-- Estado detallado de facturas -->
    <div v-if="processingStatus.length > 0" class="w-100 mt-3">
      <v-expansion-panels v-model="showProcessingDetails">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div class="d-flex align-center">
              <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
              <span>Estado de Facturas ({{ getStatusSummary() }})</span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="processing-list" style="max-height: 400px; overflow-y: auto;">
              <v-list dense>
                <v-list-item 
                  v-for="status in processingStatus" 
                  :key="status.invoice.ID_Invoice"
                  class="px-2"
                >
                  <v-list-item-avatar size="32">
                    <v-icon 
                      :color="getStatusColor(status.status)"
                      size="20"
                    >
                      {{ getStatusIcon(status.status) }}
                    </v-icon>
                  </v-list-item-avatar>
                  
                  <v-list-item-content>
                    <v-list-item-title class="text-subtitle-2">
                      {{ status.invoice.InvoiceName || `Factura ${status.invoice.ID_Invoice}` }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      ID: {{ status.invoice.ID_Invoice }} | 
                      Fecha: {{ status.invoice.InvoiceDate }} | 
                      Estado: {{ getStatusText(status.status) }}
                      <span v-if="status.error" class="error--text ml-1">
                        - {{ status.error }}
                      </span>
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-btn
                      v-if="status.status === 'failed'"
                      @click="retryInvoice(status.invoice)"
                      small
                      color="primary"
                      text
                      :loading="status.status === 'retrying'"
                    >
                      <v-icon small>mdi-refresh</v-icon>
                      Reintentar
                    </v-btn>
                    <v-btn
                      v-else-if="status.status === 'success'"
                      @click="downloadSingleInvoice(status.invoice)"
                      small
                      color="success"
                      text
                    >
                      <v-icon small>mdi-download</v-icon>
                      Descargar
                    </v-btn>
                    <v-progress-circular
                      v-else-if="status.status === 'processing' || status.status === 'retrying'"
                      indeterminate
                      size="20"
                      width="2"
                      color="primary"
                    ></v-progress-circular>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <!-- alerts -->
    <alerts
      v-if="alert.show"
      @close_alert="closeAlert"
      @accept_alert="acceptAlert"
      :properties="alert"
    >
    </alerts>

    <!-- Componente dinámico para generar PDFs (invisible) -->
    <PrintPdfInvoiceGenerator
      v-if="currentInvoiceData"
      ref="pdfGenerator"
      :invoiceData="currentInvoiceData"
      :hideQr="false"
      style="position: fixed; top: -9999px; left: -9999px; visibility: hidden;"
    />
  </div>
</template>

<script>
import Alerts from "@/components/Alerts";
import PrintPdfInvoiceGenerator from './PrintPdfInvoiceGenerator.vue';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

export default {
  name: "PrintPdfJsonProgrammatic",
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    reloadData: {
      type: Function,
      default: null
    }
  },
  components: { Alerts, PrintPdfInvoiceGenerator },

  data() {
    return {
      loading: false,
      alert: {
        type: "success",
        show: false,
        header: "",
        body: "",
      },
      download: {
        jsonFiles: false,
        pdfFiles: false,
      },
      progress: 0,
      currentInvoice: 0,
      totalInvoices: 0,
      userinfo: JSON.parse(localStorage.getItem("user")),
      branchData: {},
      // Estado de procesamiento de facturas
      processingStatus: [], // Array de objetos con el estado de cada factura
      showProcessingDetails: false,
      // Variable para el componente dinámico de generación de PDFs
      currentInvoiceData: null,
      // Cache de imágenes para optimización
      imageCache: {
        logo: null,
        banner: null,
        isLoaded: false
      },
      // Pool de componentes para evitar re-renderizado
      componentPool: {
        isReady: false,
        preRendered: false
      },
      // Batch processing para mejor rendimiento
      batchSize: 3, // Procesar en lotes más pequeños para mejor calidad
      processedBatches: 0,
      // Worker pool para procesamiento paralelo (experimental)
      useWorkers: false, // Activar cuando sea estable
      workerPool: [],
      TipoPlan: JSON.parse(localStorage.getItem("user"))?.TipoPlan || null,
    };
  },

  mounted() {
    this.getInfoBranch();
    this.configureDynamicOptimization();
  },

  methods: {
    async getInfoBranch() {
      try {
        const searchbusiness = {
          branch: JSON.parse(localStorage.getItem("user")).branch || "",
          business: JSON.parse(localStorage.getItem("user")).businessid || ""
        };

        const res = await this.$API.branches.findSpecificBranch(searchbusiness);
        const infobusiness = res[0];
        const currentBranch = JSON.parse(localStorage.getItem("branch"));
        
        this.branchData = {
          AuthFeMode: infobusiness.AuthFeMode,
          BusinessBillingNRC: infobusiness.BusinessBillingNRC,
          BusinessBillingNIT: infobusiness.BusinessBillingNIT,
          BusinessBillingName: infobusiness.BusinessBillingName,
          ...currentBranch,
        };

        // Pre-cargar y cachear imágenes una sola vez
        await this.preloadImages();

      } catch (err) {
        console.error('Error obteniendo información de sucursal:', err);
      }
    },

    async preloadImages() {
      try {
        // Solo cargar si no están en cache
        if (this.imageCache.isLoaded) {
          // Usar imágenes desde cache
          this.branchData.BranchBannerPath = this.imageCache.banner;
          this.branchData.BranchLogoPath = this.imageCache.logo;
          return;
        }

        // Cargar banner si existe
        if (this.branchData.BranchBannerPath) {
          const { imageBase64: bannerImageBase64 } = await this.$API.utilities.getImageInBase64({ 
            file: this.branchData.BranchBannerPath 
          });
          this.imageCache.banner = bannerImageBase64;
          this.branchData.BranchBannerPath = bannerImageBase64;
        }

        // Cargar logo si existe
        if (this.branchData.BranchLogoPath) {
          const { imageBase64: logoImageBase64 } = await this.$API.utilities.getImageInBase64({ 
            file: this.branchData.BranchLogoPath 
          });
          this.imageCache.logo = logoImageBase64;
          this.branchData.BranchLogoPath = logoImageBase64;
        }

        // Marcar como cargado
        this.imageCache.isLoaded = true;

      } catch (err) {
        console.error('Error pre-cargando imágenes:', err);
        // Continuar sin imágenes si hay error
      }
    },

    async downloadJsonPdf() {
      try {
        this.loading = true;
        this.$emit("setLoading", true);
        this.progress = 0;
        this.currentInvoice = 0;

        if (!this.data || !this.data.length) {
          this.showError("No se encontraron comprobantes para descargar");
          return;
        }

        this.totalInvoices = this.data.length;
        
        // Pre-cargar imágenes una sola vez antes de procesar todas las facturas
        await this.preloadImages();
        
        // Pre-renderizar componente para mejor rendimiento
        await this.preRenderComponent();
        
        // Inicializar estado de procesamiento
        this.initializeProcessingStatus();
        this.showProcessingDetails = 0; // Abrir panel automáticamente
        
        // Crear ZIP para múltiples archivos
        const zip = new JSZip();
        const pdfFolder = this.download.pdfFiles ? zip.folder("PDFs") : null;
        const jsonFolder = this.download.jsonFiles ? zip.folder("JSONs") : null;

        // Procesar en lotes para mejor rendimiento
        await this.processBatchInvoices(pdfFolder, jsonFolder);

        this.progress = 100;

        // Mostrar estadísticas finales
        const stats = this.getProcessingStats();

        // Verificar si hay archivos válidos para descargar
        let hasValidFiles = false;

        // Verificar archivos PDF
        if (pdfFolder && Object.keys(pdfFolder.files).length > 0) {
          hasValidFiles = true;
        }

        // Verificar archivos JSON
        if (jsonFolder && Object.keys(jsonFolder.files).length > 0) {
          hasValidFiles = true;
        }

        if (!hasValidFiles) {
          this.showAlert("warning", "Sin archivos válidos", 
            `No se encontraron archivos válidos para descargar.\n\n` +
            `PDFs generados: ${stats.pdfsGenerated}\n` +
            `JSONs válidos: ${stats.jsonsGenerated}\n` +
            `Facturas procesadas: ${stats.success}/${stats.total}\n\n` +
            `Revise que las facturas seleccionadas tengan datos válidos.`
          );
          return;
        }

        // Generar y descargar ZIP solo si hay archivos válidos
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const timestamp = new Date().toISOString().split('T')[0];
        saveAs(zipBlob, `facturas_${timestamp}.zip`);

        this.$emit("displaySuccessAlert");

      } catch (error) {
        console.error('Error en descarga masiva:', error);
        this.$emit("displayErrorAlert", error);
      } finally {
        this.loading = false;
        this.$emit("setLoading", false);
        this.progress = 0;
        this.currentInvoice = 0;
        this.totalInvoices = 0;
        // Limpiar datos del componente de generación
        this.currentInvoiceData = null;
        // Ejecutar limpieza completa
        this.emergencyCleanup();
      }
    },

    async getCompleteInvoiceData(invoice) {
      // Usar el nuevo endpoint unificado para una sola factura (1 petición en lugar de 4)
      try {
        const completeData = await this.$API.invoices.getCompleteSingleInvoiceData(invoice.ID_Invoice);

        // Combinar productos y servicios como en el método original
        // Usar el campo body que viene del backend (ya unificado)
        const body = completeData.body || [
          ...(completeData.invoiceProductItems?.invoiceItems || []),
          ...(completeData.invoiceServiceItems?.invoiceItems || [])
        ];

        // Usar datos de branch optimizados (con imágenes pre-cargadas)
        const optimizedBranchData = {
          ...this.branchData,
          // Asegurar que las imágenes estén disponibles
          BranchBannerPath: this.imageCache.banner || this.branchData.BranchBannerPath,
          BranchLogoPath: this.imageCache.logo || this.branchData.BranchLogoPath
        };

        return {
          data: {
            header: completeData.header,
            user: this.userinfo,
            body: body,
            branch: optimizedBranchData, // Usar datos optimizados
            dteJson: completeData.dteJsonHeaderInfo,
            docRelated: completeData.docRelated,
          }
        };

      } catch (error) {
        console.error(`Error obteniendo datos unificados para factura ${invoice.ID_Invoice}:`, error);
        // Si falla el endpoint unificado, lanzar el error para manejarlo en el procesamiento
        throw error;
      }
    },

    generateJsonData(invoiceData) {
      const { header, dteJson } = invoiceData.data;

      // Intentar obtener datos desde dteJson primero (nuevo formato), luego desde header (formato antiguo)
      const responseData = dteJson?.ResponseMhJson || header?.ResponseMhJson;
      const jsonMhData = dteJson?.JsonMh || header?.JsonMh;
      const jsonMhSigned = dteJson?.JsonMhSigned || header?.JsonMhSigned;
      
      // Solo necesitamos JsonMh para generar el archivo JSON
      // ResponseMhJson es opcional (viene de Hacienda después del procesamiento)
      if (!jsonMhData) {
        console.warn(`❌ Factura ${header?.ID_Invoice || 'desconocida'} - Sin datos JSON válidos (falta JsonMh)`);
        return { error: 'Sin datos JSON para generar archivo - falta JsonMh' };
      }

      try {
        // Parsear ResponseMhJson si existe y es string
        let selloRecibido = "";
        if (responseData) {
          const parsedResponse = typeof responseData === 'string' 
            ? JSON.parse(responseData) 
            : responseData;
          selloRecibido = parsedResponse?.selloRecibido || "";
        }
        
        const firmaElectronica = jsonMhSigned?.documento || "";
        
        // Obtener el dteJson desde el JsonMh
        const dteJsonData = jsonMhData?.dteJson || jsonMhData;
        
        // Si no hay dteJsonData dentro de JsonMh, usar JsonMh directamente
        const finalJsonData = dteJsonData || jsonMhData;
        
        const jsonToSend = { 
          ...finalJsonData
        };
        
        // Agregar campos adicionales solo si existen
        if (firmaElectronica) {
          jsonToSend.firmaElectronica = firmaElectronica;
        }
        if (selloRecibido) {
          jsonToSend.selloRecibido = selloRecibido;
        }
        
        // Limpiar campos sensibles
        delete jsonToSend.passwordPri;
        delete jsonToSend.password;
        
        return jsonToSend;
      } catch (error) {
        console.error(`Error generando JSON para factura ${header?.ID_Invoice}:`, error);
        return { error: 'Error procesando datos JSON' };
      }
    },

    generateFilename(invoice, extension) {
      const cleanName = invoice.InvoiceName
        .replace(/[:/<>*|/]/g, '')
        .replace(/"/g, '')
        .substring(0, 50); // Limitar longitud

      return `${invoice.ID_Invoice}_${invoice.InvoiceDate.replaceAll('/', '-')}_${cleanName}${extension}`;
    },

    onClickDownload() {
      this.showAlert(
        "question2",
        "Confirmar descarga",
        "¿Está seguro que desea proceder con la descarga? Este proceso puede tomar varios minutos dependiendo de la cantidad de facturas.",
        "download"
      );
    },

    showAlert(type, header, body, options = null) {
      this.alert.type = type;
      this.alert.header = header;
      this.alert.body = body;
      this.alert.show = true;
      this.alert.options = options;
    },

    closeAlert() {
      this.alert.show = false;
    },

    async acceptAlert() {
      this.alert.show = false;
      if (this.alert.options === "download") {
        await this.downloadJsonPdf();
      }
    },

    showError(message) {
      this.showAlert("warning", "Advertencia", message);
    },

    // Métodos para manejo de estado de facturas
    initializeProcessingStatus() {
      this.processingStatus = this.data.map(invoice => ({
        invoice: invoice,
        status: 'pending', // 'pending', 'processing', 'success', 'failed', 'retrying'
        error: null,
        generatedFiles: null
      }));
    },

    updateInvoiceStatus(invoiceId, status, error = null, generatedFiles = null) {
      const index = this.processingStatus.findIndex(item => item.invoice.ID_Invoice === invoiceId);
      if (index !== -1) {
        this.processingStatus[index].status = status;
        this.processingStatus[index].error = error;
        if (generatedFiles) {
          this.processingStatus[index].generatedFiles = generatedFiles;
        }
        // Forzar reactividad
        this.$set(this.processingStatus, index, { ...this.processingStatus[index] });
      }
    },

    getStatusSummary() {
      const total = this.processingStatus.length;
      const success = this.processingStatus.filter(s => s.status === 'success').length;
      const failed = this.processingStatus.filter(s => s.status === 'failed').length;
      const processing = this.processingStatus.filter(s => ['processing', 'retrying'].includes(s.status)).length;
      
      if (processing > 0) {
        return `${success} exitosas, ${failed} fallidas, ${processing} procesando`;
      }
      return `${success} exitosas, ${failed} fallidas de ${total}`;
    },

    getProcessingStats() {
      const total = this.processingStatus.length;
      const success = this.processingStatus.filter(s => s.status === 'success').length;
      const failed = this.processingStatus.filter(s => s.status === 'failed').length;
      
      // Contar archivos generados exitosamente
      let pdfCount = 0;
      let jsonCount = 0;
      
      this.processingStatus.forEach(status => {
        if (status.status === 'success' && status.generatedFiles) {
          if (status.generatedFiles.pdfBlob) pdfCount++;
          if (status.generatedFiles.jsonBlob) jsonCount++;
        }
      });

      return {
        total,
        success,
        failed,
        pdfsGenerated: pdfCount,
        jsonsGenerated: jsonCount,
        jsonValidationRate: total > 0 ? ((jsonCount / total) * 100).toFixed(1) + '%' : '0%'
      };
    },

    getStatusColor(status) {
      const colors = {
        'pending': 'grey',
        'processing': 'blue',
        'retrying': 'orange',
        'success': 'green',
        'failed': 'red'
      };
      return colors[status] || 'grey';
    },

    getStatusIcon(status) {
      const icons = {
        'pending': 'mdi-clock-outline',
        'processing': 'mdi-loading',
        'retrying': 'mdi-refresh',
        'success': 'mdi-check-circle',
        'failed': 'mdi-alert-circle'
      };
      return icons[status] || 'mdi-help-circle';
    },

    getStatusText(status) {
      const texts = {
        'pending': 'Pendiente',
        'processing': 'Procesando',
        'retrying': 'Reintentando',
        'success': 'Exitoso',
        'failed': 'Falló'
      };
      return texts[status] || 'Desconocido';
    },

    async retryInvoice(invoice) {
      try {
        this.updateInvoiceStatus(invoice.ID_Invoice, 'retrying');
        
        // Procesar la factura individual
        const success = await this.processSingleInvoice(invoice);
        
        if (success) {
          this.updateInvoiceStatus(invoice.ID_Invoice, 'success', null, success);
          this.showAlert("success", "Éxito", `Factura ${invoice.ID_Invoice} procesada exitosamente`);
        } else {
          this.updateInvoiceStatus(invoice.ID_Invoice, 'failed', 'Error desconocido en el reintento');
        }
      } catch (error) {
        console.error(`Error reintentando factura ${invoice.ID_Invoice}:`, error);
        this.updateInvoiceStatus(invoice.ID_Invoice, 'failed', error.message || 'Error en reintento');
        this.showAlert("warning", "Error", `Error reintentando factura ${invoice.ID_Invoice}: ${error.message}`);
      }
    },

    async downloadSingleInvoice(invoice) {
      try {
        // Encontrar los archivos generados para esta factura
        const statusItem = this.processingStatus.find(s => s.invoice.ID_Invoice === invoice.ID_Invoice);
        
        if (!statusItem || !statusItem.generatedFiles) {
          // Si no hay archivos generados, procesarla de nuevo
          await this.processSingleInvoice(invoice, true); // true = descargar inmediatamente
          return;
        }

        // Crear ZIP solo con esta factura
        const zip = new JSZip();
        const { pdfBlob, jsonBlob, filename } = statusItem.generatedFiles;

        let filesAdded = false;

        if (pdfBlob && this.download.pdfFiles) {
          zip.file(`${filename}.pdf`, pdfBlob);
          filesAdded = true;
        }
        if (jsonBlob && this.download.jsonFiles) {
          zip.file(`${filename}.json`, jsonBlob);
          filesAdded = true;
        }

        if (!filesAdded) {
          this.showAlert("warning", "Sin archivos", `No hay archivos válidos para descargar para la factura ${invoice.ID_Invoice}`);
          return;
        }

        // Descargar
        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, `factura_${invoice.ID_Invoice}.zip`);

        this.showAlert("success", "Descarga", `Factura ${invoice.ID_Invoice} descargada exitosamente`);
      } catch (error) {
        console.error(`Error descargando factura ${invoice.ID_Invoice}:`, error);
        this.showAlert("warning", "Error", `Error descargando factura: ${error.message}`);
      }
    },

    async processSingleInvoice(invoice, downloadImmediately = false) {
      try {
        // Obtener datos completos de la factura
        const invoiceData = await this.getCompleteInvoiceData(invoice);
        
        let pdfBlob = null;
        let jsonBlob = null;

        // Generar PDF si está seleccionado
        if (this.download.pdfFiles) {
          // LIMPIEZA COMPLETA antes de establecer nuevos datos
          this.currentInvoiceData = null;
          await this.$nextTick(); // Forzar limpieza
          
          // Establecer los datos de la factura actual para el componente
          this.currentInvoiceData = invoiceData;
          
          // Esperar al próximo tick para que Vue renderice el componente completamente
          await this.$nextTick();
          

          
          // Generar el PDF usando el componente
          const doc = await this.$refs.pdfGenerator.generatePDF();
          pdfBlob = doc.output('blob');
          
          // LIMPIEZA INMEDIATA después de generar
          this.currentInvoiceData = null;
          await this.$nextTick();
        }

        // Generar JSON si está seleccionado y los datos son válidos
        if (this.download.jsonFiles) {
          const jsonData = this.generateJsonData(invoiceData);
          
          // Solo crear el archivo JSON si los datos son válidos
          if (jsonData && !jsonData.error) {
            jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], {
              type: 'application/json'
            });
          } else {
            console.warn(`JSON inválido para factura individual ${invoice.ID_Invoice}:`, jsonData?.error || 'Datos insuficientes');
            jsonBlob = null; // Asegurar que no se genere archivo
          }
        }

        const filename = this.generateFilename(invoice, '');
        const generatedFiles = { pdfBlob, jsonBlob, filename };

        // LIMPIEZA COMPLETA después de generar todos los archivos
        await this.forceComponentCleanup();

        if (downloadImmediately) {
          // Descargar inmediatamente
          const zip = new JSZip();
          let filesAdded = false;
          
          if (pdfBlob && this.download.pdfFiles) {
            zip.file(`${filename}.pdf`, pdfBlob);
            filesAdded = true;
          }
          if (jsonBlob && this.download.jsonFiles) {
            zip.file(`${filename}.json`, jsonBlob);
            filesAdded = true;
          }

          if (filesAdded) {
            const zipBlob = await zip.generateAsync({ type: "blob" });
            saveAs(zipBlob, `factura_${invoice.ID_Invoice}.zip`);
            
            // Mostrar mensaje de éxito
            const fileTypes = [];
            if (pdfBlob && this.download.pdfFiles) fileTypes.push('PDF');
            if (jsonBlob && this.download.jsonFiles) fileTypes.push('JSON');
            
            this.showAlert("success", "Descarga exitosa", 
              `Factura ${invoice.ID_Invoice} descargada exitosamente.\n` +
              `Archivos incluidos: ${fileTypes.join(', ')}`
            );
          } else {
            console.warn(`No hay archivos válidos para descargar para la factura ${invoice.ID_Invoice}`);
            
            // Mostrar alerta explicativa
            const reasons = [];
            if (this.download.pdfFiles && !pdfBlob) reasons.push('PDF falló al generarse');
            if (this.download.jsonFiles && !jsonBlob) reasons.push('JSON no válido (falta JsonMh)');
            
            this.showAlert("warning", "Sin archivos válidos", 
              `No se pudo generar archivos válidos para la factura ${invoice.ID_Invoice}.\n\n` +
              `Razones: ${reasons.join(', ')}\n\n` +
              `Verifique que la factura tenga datos completos.`
            );
          }
        }

        return generatedFiles;
      } catch (error) {
        console.error(`Error procesando factura individual ${invoice.ID_Invoice}:`, error);
        throw error;
      }
    },

    // Método para limpiar cache de imágenes si es necesario
    clearImageCache() {
      this.imageCache = {
        logo: null,
        banner: null,
        isLoaded: false
      };
    },

    // Pre-renderizar componente para optimización
    async preRenderComponent() {
      try {
        if (this.componentPool.preRendered) return;
        
        // Crear datos dummy para pre-renderizar
        const dummyData = {
          data: {
            header: { 
              InvoiceDate: '01/01/2024',
              CodigoGeneracion: 'DUMMY',
              InvoiceType: 'FCF'
            },
            branch: this.branchData,
            body: []
          }
        };
        
        this.currentInvoiceData = dummyData;
        await this.$nextTick();
        
        this.componentPool.preRendered = true;
        
      } catch (err) {
        console.error('Error en pre-renderizado:', err);
      }
    },

    // Procesamiento en lotes optimizado
    async processBatchInvoices(pdfFolder, jsonFolder) {
      const totalBatches = Math.ceil(this.data.length / this.batchSize);
      
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const startIndex = batchIndex * this.batchSize;
        const endIndex = Math.min(startIndex + this.batchSize, this.data.length);
        const batch = this.data.slice(startIndex, endIndex);
        
        // Procesar lote actual
        await this.processBatch(batch, pdfFolder, jsonFolder, startIndex);
        
        // Pequeña pausa entre lotes para evitar bloqueo del UI
        await new Promise(resolve => setTimeout(resolve, 50));
        
        this.processedBatches++;
      }
    },

    // Procesar un lote específico
    async processBatch(batch, pdfFolder, jsonFolder, startIndex) {
      for (let i = 0; i < batch.length; i++) {
        const invoice = batch[i];
        const globalIndex = startIndex + i;
        
        this.currentInvoice = globalIndex + 1;
        this.progress = (globalIndex / this.data.length) * 100;

        try {
          // Actualizar estado: procesando
          this.updateInvoiceStatus(invoice.ID_Invoice, 'processing');

          // Obtener datos completos de la factura
          const invoiceData = await this.getCompleteInvoiceData(invoice);
          let pdfBlob = null;
          let jsonBlob = null;

          // Generar PDF si está seleccionado (optimizado)
          if (this.download.pdfFiles) {
            pdfBlob = await this.generateOptimizedPDF(invoiceData, invoice);
            if (pdfBlob && pdfFolder) {
              const filename = this.generateFilename(invoice, '.pdf');
              pdfFolder.file(filename, pdfBlob);
            }
          }

          // Generar JSON si está seleccionado y los datos son válidos
          if (this.download.jsonFiles) {
            const jsonData = this.generateJsonData(invoiceData);
            
            // Solo crear el archivo JSON si los datos son válidos (no hay error)
            if (jsonData && !jsonData.error) {
              jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], {
                type: 'application/json'
              });
              
              if (jsonBlob && jsonFolder) {
                const filename = this.generateFilename(invoice, '.json');
                jsonFolder.file(filename, jsonBlob);
              }
            } else {
              console.warn(`JSON inválido para factura ${invoice.ID_Invoice}:`, jsonData?.error || 'Datos insuficientes');
              // No generar archivo JSON, pero continuar con PDF si está seleccionado
            }
          }

          // Guardar archivos generados
          const generatedFiles = {
            pdfBlob,
            jsonBlob,
            filename: this.generateFilename(invoice, '')
          };

          // Actualizar estado: exitoso
          this.updateInvoiceStatus(invoice.ID_Invoice, 'success', null, generatedFiles);

        } catch (error) {
          console.error(`Error procesando factura ${invoice.ID_Invoice}:`, error);
          this.updateInvoiceStatus(invoice.ID_Invoice, 'failed', error.message || 'Error desconocido');
        }
      }
    },

    // Generación optimizada de PDF
    async generateOptimizedPDF(invoiceData, invoice) {
      try {
        // LIMPIEZA FORZADA antes de establecer nuevos datos
        await this.forceComponentCleanup();
        
        // Establecer datos de la factura actual
        this.currentInvoiceData = invoiceData;
        
        // Esperar a que Vue procese los cambios completamente
        await this.$nextTick();
        
        // Generar PDF con configuración ultra-optimizada
        const doc = await this.$refs.pdfGenerator.generatePDF();
        const pdfBlob = doc.output('blob');
        
        // LIMPIEZA INMEDIATA después de generar
        await this.forceComponentCleanup();
        
        return pdfBlob;
        
      } catch (error) {
        console.error(`Error generando PDF optimizado para factura ${invoice.ID_Invoice}:`, error);
        // Asegurar limpieza incluso si hay error
        await this.forceComponentCleanup();
        throw error;
      }
    },

    // Limpiar recursos entre lotes
    cleanupBetweenBatches() {
      // Forzar garbage collection si está disponible
      if (window.gc) {
        window.gc();
      }
      
      // Limpiar referencias temporales
      this.currentInvoiceData = null;
    },

    // Configuración dinámica de optimización
    configureDynamicOptimization() {
      const invoiceCount = this.data?.length || 0;
      
      if (invoiceCount > 50) {
        this.batchSize = 10;
      } else if (invoiceCount > 20) {
        this.batchSize = 7;
      } else {
        this.batchSize = 5;
      }
    },

    // Método para forzar limpieza completa del componente entre iteraciones
    async forceComponentCleanup() {
      // Limpiar datos del componente principal
      this.currentInvoiceData = null;
      
      // Limpiar cache de QR del componente hijo si existe
      if (this.$refs.pdfGenerator) {
        if (this.$refs.pdfGenerator.clearQrCache) {
          this.$refs.pdfGenerator.clearQrCache();
        }
        
        // Reinicializar datos del componente hijo
        this.$refs.pdfGenerator.numPages = 0;
        this.$refs.pdfGenerator.valuesRows = [];
        this.$refs.pdfGenerator.urlQr = null;
      }
      
      // Forzar re-renderizado completo
      await this.$nextTick();
      
      // Pequeña pausa para asegurar limpieza completa
      await new Promise(resolve => setTimeout(resolve, 10));
    },

    // Método de emergencia para limpiar todo
    emergencyCleanup() {
      this.clearImageCache();
      this.currentInvoiceData = null;
      this.processingStatus = [];
      
      // Limpiar cache de QR del componente hijo si existe
      if (this.$refs.pdfGenerator && this.$refs.pdfGenerator.clearQrCache) {
        this.$refs.pdfGenerator.clearQrCache();
      }
      
      // Forzar garbage collection
      if (window.gc) {
        window.gc();
      }
    },
  },
};
</script>

<style scoped>
.btn-add {
  background-color: #00a178 !important;
  color: white;
}

.w-100 {
  width: 100%;
}

.processing-list {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fafafa;
}

.processing-list .v-list-item {
  border-bottom: 1px solid #e0e0e0;
}

.processing-list .v-list-item:last-child {
  border-bottom: none;
}

.processing-list .v-list-item:hover {
  background-color: #f5f5f5;
}
</style>